import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { consumeContext, provideContextRecord } from '../../base/context';
import { ElementDiscoveryController } from '../../base/elements';
import { isPointerEvent, isVdsEvent, vdsEvent } from '../../base/events';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { mediaContext } from '../../media/context';
import { getSlottedChildren, raf } from '../../utils/dom';
import { clampNumber, round } from '../../utils/number';
import { rafThrottle } from '../../utils/timing';
import { isNil } from '../../utils/unit';
import { scrubberContext } from '../scrubber/context';
import { scrubberPreviewContext } from './context';
import { scrubberPreviewElementStyles } from './styles';
export const SCRUBBER_PREVIEW_ELEMENT_TAG_NAME = 'vds-scrubber-preview';
/**
 * Plugs in to the `<vds-scrubber>` component to enable media previews. A preview is essentially
 * a sneak peek of a certain time the user is interacting with on the time slider. You might
 * be familiar with this on YouTube when hovering over the scrubber, and seeing a square preview
 * above the timeline displaying an frame/image of the video at that hovered time.
 *
 * This element renders a track for the preview element to run along. There's a track fill
 * element that will fill the track up to the part of the track being interacted with. You can
 * turn this off by setting the `noTrackFill` property or `no-track-fill` attribute.
 *
 * 💡 See the `<vds-scrubber-preview-time>` element if you'd like to include preview timestamps.
 * 💡 See the `<vds-scrubber-preview-video>` element if you'd like to include low-res video previews.
 *
 * ## Previews
 *
 * You can pass in a preview to be shown while the user is interacting (hover/drag) with the
 * scrubber by passing an element into the default `slot`.
 *
 * You need to do the following on your root preview element:
 *
 * - Expect that your root preview element will be positioned absolutely.
 * - Set the `bottom` CSS property on it to adjust it to the desired position above the slider.
 * - Create CSS styles for when it has a hidden attribute (`.preview[hidden] {}`).
 *
 * The Scrubber will automatically do the following to the root preview element passed in:
 *
 * - Set the `position` to `absolute`.
 * - Set a `hidden` attribute when it should be hidden (it's left to you to hide it with CSS).
 * - Update the `translateX()` CSS property to position the preview accordingly.
 *
 * ### How do I get the current preview time?
 *
 * You can either listen to `vds-scrubber-preview-time-update` event on this component, or you can
 * use the `scrubberPreviewContext`.
 *
 * For styling you have access to the `--vds-scrubber-preview-time` CSS property which contains
 * the current time in seconds the user is previewing. There's also the `--vds-media-duration`
 * property if needed.
 *
 * @tagname vds-scrubber-preview
 * @cssprop --vds-scrubber-preview-track-height - The height of the preview track (defaults to `--vds-slider-track-height`).
 * @cssprop --vds-scrubber-preview-track-fill-bg - The background color of the track fill (defaults to `#212121`).
 * @csspart track - The element that acts as a path that the preview travels along.
 * @csspart track-fill - The element that fills the track up to the point being previewed.
 * @slot Used to pass in the element that will be displayed on preview.
 * @slot track - Used to pass content into the track element.
 * @slot track-fill - Used to pass content into the track fill element.
 * @example
 * ```html
 * <vds-scrubber>
 *   <vds-scrubber-preview>
 *     <div class="preview"></div>
 *   </vds-scrubber-preview>
 * </vds-scrubber>
 * ```
 * @example
 * ```css
 * .preview {
 *   bottom: 24px;
 *   opacity: 1;
 *   transition: opacity 0.2s ease-in;
 * }
 *
 * .preview[hidden] {
 *   display: block;
 *   opacity: 0;
 * }
 * ```
 */
export class ScrubberPreviewElement extends LitElement {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        /** @internal */
        this.ctx = provideContextRecord(this, scrubberPreviewContext);
        /**
         * Whether the preview is hidden.
         */
        this.hidden = false;
        /**
         * Whether the preview is disabled.
         */
        this.disabled = false;
        /**
         * Whether the preview track fill should be NOT be rendered.
         */
        this.noTrackFill = false;
        /**
         * Whether the preview passed in should NOT be clamped to the host element edges.
         */
        this.noClamp = false;
        this._mediaDuration = 0;
        this._isDragging = scrubberContext.dragging.initialValue;
        this._isInteracting = scrubberContext.interacting.initialValue;
        // -------------------------------------------------------------------------------------------
        // Track Fill
        // -------------------------------------------------------------------------------------------
        this._trackRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Track Fill
        // -------------------------------------------------------------------------------------------
        this._trackFillRef = createRef();
        this._dispatchPreviewTimeUpdate = rafThrottle((originalEvent) => {
            if (!this._isInteracting)
                return;
            this.dispatchEvent(vdsEvent('vds-scrubber-preview-time-update', {
                detail: this.ctx.time,
                originalEvent
            }));
        });
        this._previewPositionRafId = 0;
    }
    static get styles() {
        return [scrubberPreviewElementStyles];
    }
    static get parts() {
        return ['track', 'track-fill'];
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    connectedCallback() {
        super.connectedCallback();
        new ElementDiscoveryController(this, 'vds-scrubber-preview-connect');
    }
    update(changedProperties) {
        if (changedProperties.has('_mediaDuration')) {
            this.style.setProperty('--vds-media-duration', String(this._mediaDuration));
        }
        if (changedProperties.has('hidden') && this.hidden) {
            this.hidePreview();
        }
        if (changedProperties.has('disabled') && this.disabled) {
            this.hidePreview();
        }
        super.update(changedProperties);
    }
    render() {
        return html `${this._renderTrack()}`;
    }
    /**
     * Returns the underlying track element (`<div>`).
     */
    get trackElement() {
        return this._trackRef.value;
    }
    _renderTrack() {
        return html `
      <div
        id="track"
        part="track"
        ?hidden=${this.hidden || this.disabled}
        ${ref(this._trackRef)}
      >
        ${this._renderPreviewSlot()}${this._renderTrackFill()}${this._renderTrackSlot()}
      </div>
    `;
    }
    _renderTrackSlot() {
        return html `<slot name="track"></slot>`;
    }
    /**
     * Returns the underlying track fill element (`<div>`). This will be `undefined` if the
     * `noTrackFill` property or `no-track-fill` attribute is `true`.
     */
    get trackFillElement() {
        return this._trackFillRef.value;
    }
    _renderTrackFill() {
        if (this.noTrackFill)
            return html ``;
        return html `
      <div
        id="track-fill"
        part="track-fill"
        ?hidden=${!this._isInteracting}
        ${ref(this._trackFillRef)}
      >
        ${this._renderTrackFillSlot()}
      </div>
    `;
    }
    _renderTrackFillSlot() {
        return html `<slot name="track-fill"></slot>`;
    }
    /**
     * The element passed in to the `preview` slot.
     */
    get previewSlotElement() {
        return this._previewSlotElement;
    }
    /**
     * Whether the current slotted preview element is hidden.
     */
    get isPreviewHidden() {
        var _a, _b;
        return (_b = (_a = this.previewSlotElement) === null || _a === void 0 ? void 0 : _a.hasAttribute('hidden')) !== null && _b !== void 0 ? _b : true;
    }
    _renderPreviewSlot() {
        return html ` <slot @slotchange="${this._handlePreviewSlotChange}"></slot> `;
    }
    _handlePreviewSlotChange() {
        this._previewSlotElement = getSlottedChildren(this)[0];
        if (!isNil(this.previewSlotElement)) {
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger
                    .debugGroup('preview slot change')
                    .appendWithLabel('Preview element', this._previewSlotElement)
                    .end();
            }
            /* c8 ignore stop */
            this.previewSlotElement.style.position = 'absolute';
            this.previewSlotElement.style.left = '0';
            this.previewSlotElement.style.willChange = 'transform';
            this.hidePreview();
        }
    }
    /**
     * Show the slotted preview element.
     *
     * @param event Original event that triggered this action (if any).
     */
    showPreview(event) {
        window.clearTimeout(this._showPreviewTimeout);
        if (isNil(this.previewSlotElement) ||
            this.disabled ||
            !this._isInteracting ||
            (this._isInteracting && !this.isPreviewHidden)) {
            return;
        }
        this._showPreviewTimeout = window.setTimeout(async () => {
            var _a;
            this.ctx.showing = true;
            (_a = this.previewSlotElement) === null || _a === void 0 ? void 0 : _a.removeAttribute('hidden');
            await raf();
            if (isPointerEvent(event)) {
                await this.updatePreviewPosition(event);
            }
            await this.updateComplete;
            this.setAttribute('previewing', '');
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger
                    .debugGroup('show preview')
                    .appendWithLabel('Preview element', this._previewSlotElement)
                    .appendWithLabel('Trigger event', event)
                    .end();
            }
            /* c8 ignore stop */
            this.dispatchEvent(vdsEvent('vds-scrubber-preview-show', { originalEvent: event }));
        }, this._isDragging ? 150 : 0);
        this.requestUpdate();
    }
    /**
     * Hide the slotted preview element.
     *
     * @param event - Original event that triggered this action (if any).
     */
    async hidePreview(event) {
        var _a;
        window.clearTimeout(this._showPreviewTimeout);
        if (this.isPreviewHidden) {
            return;
        }
        if (isPointerEvent(event)) {
            this.updatePreviewPosition(event);
            await this.updateComplete;
        }
        this._dispatchPreviewTimeUpdate.cancel();
        this.ctx.showing = false;
        this.removeAttribute('previewing');
        (_a = this.previewSlotElement) === null || _a === void 0 ? void 0 : _a.setAttribute('hidden', '');
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('hide preview')
                .appendWithLabel('Preview element', this._previewSlotElement)
                .appendWithLabel('Trigger event', event)
                .end();
        }
        /* c8 ignore stop */
        this.dispatchEvent(vdsEvent('vds-scrubber-preview-hide', { originalEvent: event }));
        this.requestUpdate();
    }
    _updatePreviewTime(time, event) {
        this.ctx.time = clampNumber(0, round(time, 5), this._mediaDuration);
        this.style.setProperty('--vds-scrubber-preview-time', String(this.ctx.time));
        this._dispatchPreviewTimeUpdate(event);
    }
    /**
     * @param thumbPosition - `pointerEvent.clientX`
     */
    _calcPercentageOfTrackAtThumbPosition(thumbPosition) {
        if (isNil(this.trackElement))
            return 0;
        const trackRect = this.trackElement.getBoundingClientRect();
        const percentage = (100 / trackRect.width) * (thumbPosition - trackRect.left);
        return clampNumber(0, percentage, 100);
    }
    /**
     * @param percentage - the percentage of the track to position the preview at.
     */
    _calcPreviewXPosition(percentage) {
        if (isNil(this.previewSlotElement) || isNil(this.trackElement))
            return 0;
        const { width: trackWidth } = this.trackElement.getBoundingClientRect();
        const { width: previewWidth } = this.previewSlotElement.getBoundingClientRect();
        const left = (percentage / 100) * trackWidth - previewWidth / 2;
        const rightLimit = trackWidth - previewWidth;
        return this.noClamp ? left : clampNumber(0, left, rightLimit);
    }
    /**
     * Updates the slotted preview element position given a `PointerEvent`, or `VdsCustomEvent` with
     * an `originalEvent` property referencing a `PointerEvent`.
     *
     * @param event
     */
    async updatePreviewPosition(event) {
        window.cancelAnimationFrame(this._previewPositionRafId);
        this._previewPositionRafId = await raf(() => {
            const pointerEvent = isVdsEvent(event) ? event.originalEvent : event;
            if (!isPointerEvent(pointerEvent))
                return;
            const percent = this._calcPercentageOfTrackAtThumbPosition(pointerEvent.clientX);
            const time = (percent / 100) * this._mediaDuration;
            this._updatePreviewTime(time, event);
            if (!isNil(this.previewSlotElement)) {
                const xPos = this._calcPreviewXPosition(percent);
                this.previewSlotElement.style.transform = `translateX(${xPos}px)`;
            }
        });
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], ScrubberPreviewElement.prototype, "hidden", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ScrubberPreviewElement.prototype, "disabled", void 0);
__decorate([
    property({ attribute: 'no-track-fill', type: Boolean })
], ScrubberPreviewElement.prototype, "noTrackFill", void 0);
__decorate([
    property({ attribute: 'no-clamp', type: Boolean })
], ScrubberPreviewElement.prototype, "noClamp", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.duration, { transform: (d) => (d >= 0 ? d : 0) })
], ScrubberPreviewElement.prototype, "_mediaDuration", void 0);
__decorate([
    state(),
    consumeContext(scrubberContext.dragging)
], ScrubberPreviewElement.prototype, "_isDragging", void 0);
__decorate([
    state(),
    consumeContext(scrubberContext.interacting)
], ScrubberPreviewElement.prototype, "_isInteracting", void 0);
